import React, { useEffect, useState } from "react";
import { Modules } from "@interfaces/common";
import { $UserAddPermissions, ACTIONS, setModule } from "@store/user-store";
import { WorkspaceDirectoryShortDataT } from "@interfaces/company/workspace";
import { setTitle } from "@store/title-store";
import { TitleProps } from "@store/title-store";
import { useDocumentTemplatesGroupsQuery } from "@lib/document-templates";
import { useNavigate, useLocation } from "react-router-dom";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";
import { useQueryParam } from "@hooks/useQueryParam";
import qs from "qs";

import { DocumentTemplatesTable } from "./components";
import DocumentTemplatesGroups from "@components/company/document-templates/document-templates-groups";
import { ColorfulButton } from "@components/common/common";
import clsx from "clsx";
import style from "@scss/pages/company/company-workspace.module.scss";
import styles from "./document-templates.module.scss";

export const DocumentTemplates: React.FC = () => {
  const permissions = useUnit($UserAddPermissions);
  const history = useNavigate();
  const { pathname, search } = useLocation();
  const { groups } = useDocumentTemplatesGroupsQuery();
  const [paths, setPaths] = useState<WorkspaceDirectoryShortDataT[]>([]);

  const groupId = useQueryParam("group_id");
  const activeGroupId = groupId ? parseInt(groupId) : groups?.[0]?.id;

  const { open } = useModal();

  useEffect(() => {
    const title = [
      { title: "Главная", href: "/" },
      { title: "Дополнительная информация", href: "/additional" },
      { title: "Шаблоны документов", href: "/additional/document-templates" },
    ] as TitleProps[];
    const urlParams = new URLSearchParams(search);
    if (urlParams.has("group_id")) {
      const group_id = parseInt(urlParams.get("group_id")!);
      const group = groups?.find((e) => e.id === group_id);
      if (group)
        title.push({
          title: group.name,
          href: `${pathname}?group_id=${urlParams.get("group_id")}`,
        });

      if (urlParams.has("folder_id") && paths.length) {
        paths
          .filter((e) => e !== undefined)
          .forEach((e, i) => {
            const disabled =
              i < paths.filter((e) => e !== undefined).length - 2;
            title.push({
              title: e.name,
              href: `${pathname}?group_id=${urlParams.get(
                "group_id"
              )}&folder_id=${e.id}`,
              disabled,
            });
          });
      }
    }
    setTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setActiveGroupId = (groupId: number | null) => {
    if (groupId === null) {
      return history(pathname);
    }

    history({
      pathname: pathname,
      search: qs.stringify({ group_id: groupId }),
    });
  };

  useEffect(() => {
    setModule(Modules.WORKSPACE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groups && groups.length === 0 && activeGroupId) {
      setActiveGroupId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, activeGroupId]);

  return (
    <div className={styles.content}>
      <div className={clsx(style.workspace_top_panel)}>
        {groups && activeGroupId && (
          <DocumentTemplatesGroups
            groups={groups}
            activeGroupId={activeGroupId}
            setActiveGroupId={setActiveGroupId}
          />
        )}

        <div className={clsx(style.workspace_buttons)}>
          {permissions.hasPermission(
            ACTIONS.companyDocumentTemplates_groups_allowedToCreate
          ) && (
            <>
              <ColorfulButton
                text="Создать группу"
                onClick={() =>
                  open("CreateDocumentTemplatesGroupModal", {
                    btnText: "Создать",
                    modalData: { modalTitle: "Создать группу" },
                  })
                }
              />
              {permissions.hasPermission(
                ACTIONS.companyDocumentTemplates_directories_allowedToCreate
              ) && (
                <ColorfulButton
                  text="Создать папку"
                  plusIcon={false}
                  onClick={() =>
                    open("CreateFolderDocumentTemplatesModal", {
                      btnText: "Создать",
                      modalData: {
                        modalTitle: "Создать папку",
                        groupId: activeGroupId,
                      },
                    })
                  }
                />
              )}
            </>
          )}
          {permissions.hasPermission(
            ACTIONS.companyDocumentTemplates_files_allowedToCreate
          ) && (
            <ColorfulButton
              text="Загрузить документы"
              plusIcon={false}
              onClick={() => open("UploadDocumentTemplatesFileModal")}
            />
          )}
          {permissions.hasPermission(
            ACTIONS.companyDocumentTemplates_allowedToShowArchive
          ) && (
            <ColorfulButton
              text="Архив"
              plusIcon={false}
              link="/additional/document-templates/archive"
            />
          )}
        </div>
      </div>
      <div className={style.workspace_table}>
        <DocumentTemplatesTable
          groupId={activeGroupId ?? 0}
          groupsCount={groups?.length ?? 0}
          setPathsMain={setPaths}
        />
      </div>
    </div>
  );
};
